import React, { useState, useEffect } from 'react';
import { csAdminApi } from '../../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  PersonOutlined as PersonIcon,
  CheckCircleOutlined as CheckIcon,
  CancelOutlined as NotEligibleIcon,
} from '@mui/icons-material';

const COMMITTEE_ROLES = [
  { value: 'chairman', label: 'Chairman' },
  { value: 'member', label: 'Member' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'observer', label: 'Observer' },
];

const AddMemberModal = ({ open, onClose, onAdd, committeeId, committeeName, existingMembers = [], editMember = null }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [availableDirectors, setAvailableDirectors] = useState([]);
  const [filteredDirectors, setFilteredDirectors] = useState([]);
  const [selectedDirector, setSelectedDirector] = useState(null);
  const [role, setRole] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editMember;

  // Helper to get role value from label
  const getRoleValue = (roleLabel) => {
    if (!roleLabel) return '';
    const found = COMMITTEE_ROLES.find(r => 
      r.label.toLowerCase() === roleLabel.toLowerCase() ||
      r.value === roleLabel.toLowerCase()
    );
    return found ? found.value : roleLabel.toLowerCase();
  };

  // Initialize form with edit data
  useEffect(() => {
    if (open && editMember) {
      // Set form data from edit member
      setRole(getRoleValue(editMember.role));
      
      // Format dates for input[type="date"]
      let formattedAppointmentDate = '';
      if (editMember.appointmentDate || editMember.joinedDate || editMember.termStart) {
        try {
          const dateStr = editMember.appointmentDate || editMember.joinedDate || editMember.termStart;
          formattedAppointmentDate = new Date(dateStr).toISOString().split('T')[0];
        } catch {
          formattedAppointmentDate = '';
        }
      }
      setAppointmentDate(formattedAppointmentDate);

      let formattedTermEndDate = '';
      if (editMember.termEndDate || editMember.termEnd) {
        try {
          const dateStr = editMember.termEndDate || editMember.termEnd;
          formattedTermEndDate = new Date(dateStr).toISOString().split('T')[0];
        } catch {
          formattedTermEndDate = '';
        }
      }
      setTermEndDate(formattedTermEndDate);

      setNotes(editMember.notes || '');

      // Create a mock selected director from the edit member data
      setSelectedDirector({
        id: editMember.directorId || editMember.id,
        fullName: editMember.fullName || editMember.name,
        position: editMember.position || editMember.directorPosition || '',
        isEligible: true,
      });
    } else if (open && !editMember) {
      // Reset form for new member
      setRole('');
      setAppointmentDate('');
      setTermEndDate('');
      setNotes('');
      setSelectedDirector(null);
    }
  }, [open, editMember]);

  // Fetch available directors on open (only for new members)
  useEffect(() => {
    if (open && !isEditing) {
      fetchAvailableDirectors();
    }
  }, [open, committeeId, isEditing]);

  // Filter directors based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredDirectors(availableDirectors);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = availableDirectors.filter(director =>
        director.fullName.toLowerCase().includes(query) ||
        (director.position || '').toLowerCase().includes(query) ||
        (director.email || '').toLowerCase().includes(query) ||
        (director.emiratesId || '').toLowerCase().includes(query)
      );
      setFilteredDirectors(filtered);
    }
  }, [searchQuery, availableDirectors]);

  const fetchAvailableDirectors = async () => {
    setLoading(true);
    setError(null);

    try {
      const existingMemberIds = existingMembers.flatMap((member) =>
        [member.id, member.directorId].filter((value) => value !== undefined && value !== null)
      );

      if (!committeeId) {
        setAvailableDirectors([]);
        setFilteredDirectors([]);
        return;
      }

      const res = await csAdminApi.getCommitteeAvailableDirectors(committeeId);
        const directors = (res.data?.data || [])
          .map((d) => ({
            id: d.id || d.directorId,
            directorId: d.directorId || d.id,
            personId: d.personId || d.person_id || null,
            fullName: d.fullName || d.full_name,
            position: d.position || 'Director',
            email: d.email || '',
            emiratesId: d.emiratesId || d.emirates_id || '',
            committeeCount: Number(d.committeeCount) || 0,
            isEligible: d.isEligible !== false,
          }))
          .filter((d) => !existingMemberIds.includes(d.id));

        const uniqueDirectors = [];
        const seenKeys = new Set();
        directors.forEach((director) => {
          const emiratesKey = String(director.emiratesId || '').replace(/\s+/g, '').toLowerCase();
          const key = director.personId
            ? `person:${director.personId}`
            : emiratesKey && !emiratesKey.startsWith('pending')
              ? `eid:${emiratesKey}`
              : `director:${director.directorId || director.id}`;
          if (seenKeys.has(key)) return;
          seenKeys.add(key);
          uniqueDirectors.push(director);
        });

      setAvailableDirectors(uniqueDirectors);
      setFilteredDirectors(uniqueDirectors);
    } catch (err) {
      setError('Failed to load directors. Please try again.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!selectedDirector) {
      setError('Please select a director');
      return false;
    }
    if (!role) {
      setError('Please select a role');
      return false;
    }
    if (!appointmentDate) {
      setError('Please select an appointment date');
      return false;
    }
    if (termEndDate && new Date(termEndDate) <= new Date(appointmentDate)) {
      setError('Term end date must be after appointment date');
      return false;
    }

    // Check if role is already taken (for Chairman/Secretary) - skip check when editing same member's role
    if ((role === 'chairman' || role === 'secretary') && existingMembers) {
      const roleExists = existingMembers.some(member => 
        member.role?.toLowerCase() === role && 
        (!editMember || member.id !== editMember.id)
      );
      if (roleExists) {
        setError(`The ${role} position is already filled. Please select a different role.`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        directorId: selectedDirector.id,
        memberName: selectedDirector.fullName,
        role,
        appointmentDate,
        termEndDate,
        notes,
      };

      if (committeeId) {
        if (isEditing && editMember?.id) {
          await csAdminApi.updateCommitteeMember(committeeId, editMember.id, payload);
        } else {
          await csAdminApi.addCommitteeMember(committeeId, payload);
        }
      }

      const memberData = {
        id: editMember?.id || Date.now(),
        directorId: selectedDirector.id,
        fullName: selectedDirector.fullName,
        position: selectedDirector.position,
        role,
        appointmentDate,
        termEndDate,
        notes,
        status: editMember?.status || 'active',
      };

      if (onAdd) {
        onAdd(memberData);
      }

      handleClose();
    } catch (err) {
      setError('Failed to add member. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectDirector = (director) => {
    if (!director.isEligible) {
      setError(director.ineligibilityReason || 'This director is not eligible');
      return;
    }
    setSelectedDirector(director);
    setError(null);
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedDirector(null);
    setRole('');
    setAppointmentDate('');
    setTermEndDate('');
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Committee Member' : 'Add Committee Member'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Committee Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Adding member to:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {committeeName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current members: {existingMembers?.length || 0}
          </Typography>
        </Box>

        {/* Search Directors - Only show when adding new member */}
        {!isEditing && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Select Director <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Search for director..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={submitting || loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading directors...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Available Directors ({filteredDirectors.length}):
                  </Typography>
                  <List
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto',
                      bgcolor: 'background.paper',
                    }}
                  >
                    {filteredDirectors.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary="No directors found"
                          secondary="Try adjusting your search or add a new director"
                          sx={{ textAlign: 'center', py: 2 }}
                        />
                      </ListItem>
                    ) : (
                      filteredDirectors.map((director) => (
                        <ListItem
                          key={director.id}
                          button
                          selected={selectedDirector?.id === director.id}
                          onClick={() => handleSelectDirector(director)}
                          disabled={!director.isEligible || submitting}
                          sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                            opacity: director.isEligible ? 1 : 0.6,
                          }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={selectedDirector?.id === director.id}
                              disabled={!director.isEligible}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight={500}>
                                  {director.fullName}
                                </Typography>
                                {!director.isEligible && (
                                  <Chip
                                    label="Not Eligible"
                                    size="small"
                                    color="error"
                                    icon={<NotEligibleIcon />}
                                  />
                                )}
                                {director.isEligible && director.committeeCount === 0 && (
                                  <Chip
                                    label="No committees"
                                    size="small"
                                    color="success"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {[director.position, director.email].filter(Boolean).join(' • ')}
                                  {director.committeeCount > 0
                                    ? ` • On ${director.committeeCount} other committee${director.committeeCount !== 1 ? 's' : ''}`
                                    : ' • Not currently on any committee'}
                                </Typography>
                                {!director.isEligible && director.ineligibilityReason && (
                                  <Typography variant="caption" color="error.main">
                                    {director.ineligibilityReason}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))
                    )}
                  </List>
                </>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />
          </>
        )}

        {/* Selected Director Info */}
        {selectedDirector && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CheckIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600} color="primary.main">
                Selected Director
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={600}>
              {selectedDirector.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedDirector.position}
            </Typography>
          </Box>
        )}

        {/* Role Selection */}
        <FormControl fullWidth required sx={{ mb: 3 }}>
          <InputLabel>Role in Committee</InputLabel>
          <Select
            value={role}
            label="Role in Committee"
            onChange={(e) => setRole(e.target.value)}
            disabled={submitting || !selectedDirector}
          >
            {COMMITTEE_ROLES.map((roleOption) => (
              <MenuItem key={roleOption.value} value={roleOption.value}>
                {roleOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Appointment Date */}
        <TextField
          fullWidth
          required
          label="Appointment Date"
          type="date"
          value={appointmentDate}
          onChange={(e) => setAppointmentDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={submitting}
          sx={{ mb: 3 }}
        />

        {/* Term End Date */}
        <TextField
          fullWidth
          label="Term End Date (optional)"
          type="date"
          value={termEndDate}
          onChange={(e) => setTermEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          disabled={submitting}
          helperText="Leave empty for indefinite term"
          sx={{ mb: 3 }}
        />

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={3}
          placeholder="Add any additional notes or comments"
          disabled={submitting}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !selectedDirector || !role || !appointmentDate}
        >
          {submitting ? (isEditing ? 'Updating...' : 'Adding Member...') : (isEditing ? 'Update Member' : 'Add Member')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberModal;
